
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import getRoleBasedRedirectPath from "@/lib/roleRedirects";
import SuperAdminCreator from "@/components/admin/SuperAdminCreator";
import { Mail, Shield, Phone } from "lucide-react";
import { validateGhanaPhone, cleanPhoneInput } from "@/utils/phoneUtils";
import CustomerService from "@/services/customerService";
import { smsService } from "@/services/smsService";
import { emailService } from "@/services/emailService";

// Signup schema for customers
const signUpSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(6, "Password must be at least 6 characters"),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone: z.string().min(9, "Phone number is required").refine(
    (phone) => validateGhanaPhone(phone).isValid,
    { message: "Please enter a valid Ghana phone number" }
  ),
});

const signInSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(1, "Password is required"),
});

const magicLinkSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

type SignUpFormValues = z.infer<typeof signUpSchema>;
type SignInFormValues = z.infer<typeof signInSchema>;
type MagicLinkFormValues = z.infer<typeof magicLinkSchema>;
type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [defaultTab, setDefaultTab] = useState("signin");

  const [showAdminCreator, setShowAdminCreator] = useState(false);
  const { data: userRoles } = useUserRoles(user?.id);

  // Set default tab based on URL parameter
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'signup') {
      setDefaultTab('signup');
    }
  }, [searchParams]);

  // Check if user is already logged in and redirect accordingly
  useEffect(() => {
    if (user && userRoles) {
      const rolesList = userRoles.map(r => typeof r === 'string' ? r : r.role);
      const redirectPath = getRoleBasedRedirectPath(rolesList);
      navigate(redirectPath);
    }
  }, [user, userRoles, navigate]);

  const signUpForm = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: "", password: "", first_name: "", last_name: "", phone: "" },
  });
  
  const signInForm = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });

  const magicLinkForm = useForm<MagicLinkFormValues>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: { email: "" },
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const handleSignUp = async (values: SignUpFormValues) => {
    setLoading(true);
    
    try {
      const { email, password, first_name, last_name, phone } = values;
      
      // Validate phone number
      const phoneValidation = validateGhanaPhone(phone);
      if (!phoneValidation.isValid) {
        toast({
          title: "Phone Number Error",
          description: phoneValidation.errorMessage || "Please enter a valid phone number",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Check if customer already exists with this phone number
      const existingCustomer = await CustomerService.findCustomerByPhone(phoneValidation.normalizedPhone);
      
      // Create the user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
            phone: phoneValidation.normalizedPhone,
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (authError) throw authError;

      // Check if email confirmation is required (session is null)
      if (authData.user && !authData.session) {
        toast({
          title: "Account Created - Confirmation Required",
          description: "Please check your email to confirm your account before signing in.",
        });
        setLoading(false);
        // Switch to sign in tab or clear form
        return;
      }

      // If signup was successful and we have a session (auto-login)
      if (authData.user && authData.session) {
        try {
          // If customer already exists, link the user account to that customer
          if (existingCustomer) {
            await CustomerService.linkUserToCustomer(authData.user.id, phoneValidation.normalizedPhone);
            
            toast({
              title: "Account Created!",
              description: `Welcome back! Your account has been linked to your existing customer profile. You have ${existingCustomer.total_orders} previous orders.`,
            });
          } else {
            // Create new customer profile for this user
            await CustomerService.findOrCreateCustomer({
              firstName: first_name,
              lastName: last_name,
              email: email,
              phone: phoneValidation.normalizedPhone,
              customerType: 'individual',
              userId: authData.user.id,
              notes: 'Customer profile created during account signup'
            });

            // Send welcome SMS (primary) with email fallback
            const customerName = `${first_name} ${last_name}`.trim();
            let welcomeMessageSent = false;

            try {
              // Try SMS first
              // Built and sent by the send-sms function, to this account's own phone
              const smsResult = await smsService.sendWelcome();

              if (smsResult.success) {
                welcomeMessageSent = true;
                console.log('Welcome SMS sent successfully');
              } else {
                console.error('SMS failed, trying email fallback:', smsResult.error);
              }
            } catch (smsError) {
              console.error('SMS service error:', smsError);
            }

            // Email fallback if SMS failed
            if (!welcomeMessageSent) {
              try {
                const emailResult = await emailService.sendWelcomeEmail(email, customerName);
                if (emailResult.success) {
                  welcomeMessageSent = true;
                  console.log('Welcome email sent as fallback');
                } else {
                  console.error('Welcome email also failed:', emailResult.error);
                }
              } catch (emailError) {
                console.error('Email service error:', emailError);
              }
            }
            
            toast({
              title: "Account Created!",
              description: welcomeMessageSent 
                ? "Welcome to Dough Man Foods! Check your messages for welcome instructions."
                : "Welcome to Dough Man Foods! Check your email for a confirmation link.",
            });
          }
        } catch (customerError) {
          console.error('Error linking/creating customer:', customerError);
          // Don't fail the entire signup if customer linking fails
          toast({
            title: "Account Created!",
            description: "Check your email for a confirmation link. Customer profile will be created on first order.",
          });
        }
      } else {
        toast({
          title: "Success!",
          description: "Check your email for a confirmation link.",
        });
      }

      signUpForm.reset();
      
    } catch (error: unknown) {
      console.error('Signup error:', error);
      toast({
        title: "Sign Up Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (values: SignInFormValues) => {
    setLoading(true);
    const { email, password } = values;

    try {
      // Log the login attempt
      console.log('Attempting to sign in with:', { email });
      console.log('Using Supabase URL:', import.meta.env.VITE_SUPABASE_URL);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        throw error;
      }

      if (!data.user) {
        throw new Error('No user data returned');
      }

      console.log('Sign in successful!');

      // Fetch user roles BEFORE clearing loading state
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.user.id);

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        // Default to customer if role fetch fails
        navigate("/");
        return;
      }

      // Use the proper role-based landing page logic
      const userRoles = roles?.map(r => r.role) || [];
      console.log('User roles:', userRoles);

      const redirectPath = getRoleBasedRedirectPath(userRoles);
      console.log('Redirecting to:', redirectPath);
      // Navigate and THEN clear loading
      navigate(redirectPath);

    } catch (error: unknown) {
      console.error('Login error:', error);
      toast({
        title: "Sign In Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      // Only clear loading after navigation attempt
      setLoading(false);
    }
  };

  const handleMagicLink = async (values: MagicLinkFormValues) => {
    setLoading(true);
    const { email } = values;
    
    // Get the current site URL from environment or window.location.origin
    const siteUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUrl = `${siteUrl}/auth-redirect`;
    
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    
    setLoading(false);
    if (error) {
      toast({
        title: "Magic Link Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Magic Link Sent!",
        description: "Check your email for the login link. You'll be redirected automatically after login.",
      });
      magicLinkForm.reset();
    }
  };

  const handleForgotPassword = async (values: ForgotPasswordFormValues) => {
    setLoading(true);
    const { email } = values;
    
    // Get the current site URL from environment or window.location.origin
    const siteUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUrl = `${siteUrl}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    
    setLoading(false);
    if (error) {
      toast({
        title: "Password Reset Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for a link to reset your password.",
      });
      forgotPasswordForm.reset();
    }
  };

 
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <Tabs defaultValue={defaultTab} value={defaultTab} onValueChange={setDefaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
            <TabsTrigger value="magic">Magic Link</TabsTrigger>
            <TabsTrigger value="forgot">Reset Password</TabsTrigger>
          </TabsList>
          
          <TabsContent value="signin">
            <Card>
              <CardHeader>
                <CardTitle>Sign In</CardTitle>
                <CardDescription>
                  Enter your credentials to access your account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input id="signin-email" type="email" placeholder="m@example.com" data-testid="email-input" {...signInForm.register("email")} />
                    {signInForm.formState.errors.email && <p className="text-sm text-red-500" data-testid="email-error">{signInForm.formState.errors.email.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input id="signin-password" type="password" data-testid="password-input" {...signInForm.register("password")} />
                     {signInForm.formState.errors.password && <p className="text-sm text-red-500" data-testid="password-error">{signInForm.formState.errors.password.message}</p>}
                  </div>
                  <Button type="submit" className="w-full" data-testid="login-button" disabled={loading}>
                    {loading ? "Signing In..." : "Sign In"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="signup">
            <Card>
              <CardHeader>
                <CardTitle>Create Account</CardTitle>
                <CardDescription>
                  Sign up with your email and password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-first-name">First Name</Label>
                      <Input 
                        id="signup-first-name" 
                        placeholder="Kwame"
                        {...signUpForm.register("first_name")} 
                      />
                      {signUpForm.formState.errors.first_name && (
                        <p className="text-sm text-red-500">{signUpForm.formState.errors.first_name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-last-name">Last Name</Label>
                      <Input 
                        id="signup-last-name" 
                        placeholder="Asante"
                        {...signUpForm.register("last_name")} 
                      />
                      {signUpForm.formState.errors.last_name && (
                        <p className="text-sm text-red-500">{signUpForm.formState.errors.last_name.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input 
                      id="signup-email" 
                      type="email" 
                      placeholder="kwame@example.com" 
                      {...signUpForm.register("email")} 
                    />
                    {signUpForm.formState.errors.email && (
                      <p className="text-sm text-red-500">{signUpForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">Phone Number</Label>
                    <Input 
                      id="signup-phone" 
                      type="tel" 
                      placeholder="0241234567" 
                      {...signUpForm.register("phone", {
                        onChange: (e) => {
                          e.target.value = cleanPhoneInput(e.target.value);
                        }
                      })} 
                    />
                    {signUpForm.formState.errors.phone && (
                      <p className="text-sm text-red-500">{signUpForm.formState.errors.phone.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input 
                      id="signup-password" 
                      type="password" 
                      {...signUpForm.register("password")} 
                    />
                    {signUpForm.formState.errors.password && (
                      <p className="text-sm text-red-500">{signUpForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Creating Account..." : "Sign Up"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="magic">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Magic Login
                </CardTitle>
                <CardDescription>
                  Get a secure login link sent to your email. Simple and password-free access for everyone.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={magicLinkForm.handleSubmit(handleMagicLink)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="magic-email">Email</Label>
                    <Input 
                      id="magic-email" 
                      type="email" 
                      placeholder="admin@example.com" 
                      {...magicLinkForm.register("email")} 
                    />
                    {magicLinkForm.formState.errors.email && (
                      <p className="text-sm text-red-500">{magicLinkForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    <Mail className="mr-2 h-4 w-4" />
                    {loading ? "Sending..." : "Send Magic Link"}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Magic links provide secure, password-free access to your account
                  </p>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="forgot">
            <Card>
              <CardHeader>
                <CardTitle>Reset Password</CardTitle>
                <CardDescription>
                  Enter your email to receive a password reset link.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input 
                      id="forgot-email" 
                      type="email" 
                      placeholder="your@email.com" 
                      {...forgotPasswordForm.register("email")} 
                    />
                    {forgotPasswordForm.formState.errors.email && (
                      <p className="text-sm text-red-500">{forgotPasswordForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Sending Reset Link..." : "Send Reset Link"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Secret Admin Creator Toggle */}
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdminCreator(!showAdminCreator)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            <Shield className="mr-1 h-3 w-3" />
            Admin Tools
          </Button>
        </div>

        {/* Super Admin Creator */}
        {showAdminCreator && <SuperAdminCreator />}
      </div>
    </div>
  );
};

export default AuthPage;
